import { useState, useEffect, useRef } from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import { getTranslation } from "../../translations";
import useFadeIn from "../../hooks/useFadeIn";
import "../../hooks/useFadeIn.css";
import "./FeatureSection.css";

const videoSources = [
  "/videos/vision_3.1-1.mp4",
  "/videos/vision_3.2-1.mp4",
  "/videos/vision_3.3-1.mp4",
];

function FeatureSection() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const videoRefs = useRef([]);
  const trackRef = useRef(null);
  const { language } = useLanguage();
  const t = (key) => getTranslation(language, key);

  const headerFade = useFadeIn();

  const extendedVideos = [
    videoSources[videoSources.length - 1],
    ...videoSources,
    videoSources[0],
  ];
  const adjustedIndex = currentIndex + 1;

  const goToPrev = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => prev - 1);
  };

  const goToNext = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => prev + 1);
  };



  useEffect(() => {
    if (currentIndex === videoSources.length) {
      setTimeout(() => {
        if (trackRef.current) {
          trackRef.current.style.transition = "none";
        }
        setCurrentIndex(0);
        setTimeout(() => {
          if (trackRef.current) {
            trackRef.current.style.transition = "transform 0.8s ease-in-out";
          }
        }, 50);
      }, 800);
    } else if (currentIndex === -1) {
      setTimeout(() => {
        if (trackRef.current) {
          trackRef.current.style.transition = "none";
        }
        setCurrentIndex(videoSources.length - 1);
        setTimeout(() => {
          if (trackRef.current) {
            trackRef.current.style.transition = "transform 0.8s ease-in-out";
          }
        }, 50);
      }, 800);
    }
  }, [currentIndex]);

  useEffect(() => {
    if (isTransitioning) {
      const timer = setTimeout(() => {
        setIsTransitioning(false);
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [currentIndex, isTransitioning]);

  useEffect(() => {
    videoRefs.current.forEach((video, index) => {
      if (video) {
        if (index === adjustedIndex) {
          video.currentTime = 0;
          const playPromise = video.play();
          if (playPromise !== undefined) {
            playPromise.catch(() => {
              // Auto-play was prevented or interrupted, ignore error
            });
          }
        } else {
          video.pause();
        }
      }
    });
  }, [adjustedIndex]);

  const overlayLabels = t('feature.overlayLabels');
  const descriptionLines = t('feature.description').split('\n');

  return (
    <section className="feature-section">
      <div
        ref={headerFade.ref}
        className={`feature-header fade-in-element ${headerFade.isVisible ? 'visible' : ''}`}
      >
        <span className="feature-label">{t('feature.label')}</span>
        <h2 className="feature-title">{t('feature.title')}</h2>
        <p className="feature-description">
          {descriptionLines.map((line, index) => (
            <span key={index}>
              {line}
              {index < descriptionLines.length - 1 && <br />}
            </span>
          ))}
        </p>
      </div>

      <div className="feature-carousel">
        <div
          ref={trackRef}
          className="carousel-track"
          style={{ transform: `translateX(-${adjustedIndex * 100}%)` }}
        >
          {extendedVideos.map((src, index) => (
            <div key={index} className="carousel-slide">
              <video
                ref={(el) => (videoRefs.current[index] = el)}
                className="carousel-video"
                muted
                playsInline
                preload="auto"
                onEnded={goToNext}
              >
                <source src={src} type="video/mp4" />
              </video>
            </div>
          ))}
        </div>
        <div className="carousel-overlay">

          <div className="overlay-gradient-bottom" />
          <div className="overlay-content">
            <p className="overlay-text-top">{t('feature.overlayTop')}</p>
            <p className="overlay-text-bottom">
              {t('feature.overlayPrefix')}
              <span className="overlay-highlight">
                {overlayLabels[((currentIndex % videoSources.length) + videoSources.length) % videoSources.length]}
              </span>
              {' '}
              {t('feature.overlayBottom')}
            </p>
            <div className="carousel-indicators">
              {videoSources.map((_, index) => (
                <div
                  key={index}
                  className={`indicator-dot ${index === ((currentIndex % videoSources.length) + videoSources.length) % videoSources.length
                      ? "active"
                      : ""
                    }`}
                  onClick={() => {
                    if (!isTransitioning && currentIndex !== index) {
                      setIsTransitioning(true);
                      setCurrentIndex(index);
                    }
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default FeatureSection;
